import type { FC } from 'react';
import { Box, Card, CardContent, Alert, CircularProgress } from '@mui/material';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import {
  selectOnboarding,
  nextStep,
  prevStep,
  submitIntakeAsync,
} from '../../../store/onboardingSlice';
import StepIndicator from './StepIndicator';
import BusinessInfoStep from './BusinessInfoStep';
import ComplianceStep from './ComplianceStep';
import IntegrationsStep from './IntegrationsStep';
import PhoneConfigStep from './PhoneConfigStep';
import ReviewStep from './ReviewStep';

const IntakeWizard: FC = () => {
  const dispatch = useAppDispatch();
  const { currentStep, intake, loading, saving, error } = useAppSelector(selectOnboarding);

  const steps = [
    { number: 1, label: 'Business Info' },
    { number: 2, label: 'Compliance' },
    { number: 3, label: 'Integrations' },
    { number: 4, label: 'Phone Setup' },
    { number: 5, label: 'Review' },
  ];

  const handleNext = () => {
    dispatch(nextStep());
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handlePrev = () => {
    dispatch(prevStep());
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubmit = async () => {
    try {
      await dispatch(submitIntakeAsync(intake as any)).unwrap();
    } catch (err) {
      // Error handled by Redux
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return <BusinessInfoStep onNext={handleNext} />;
      case 2:
        return <ComplianceStep onNext={handleNext} onPrev={handlePrev} />;
      case 3:
        return <IntegrationsStep onNext={handleNext} onPrev={handlePrev} />;
      case 4:
        return <PhoneConfigStep onNext={handleNext} onPrev={handlePrev} />;
      case 5:
        return <ReviewStep onPrev={handlePrev} onSubmit={handleSubmit} />;
      default:
        return <BusinessInfoStep onNext={handleNext} />;
    }
  };

  return (
    <Box>
      {/* Step Indicator */}
      <StepIndicator steps={steps} currentStep={currentStep} />

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Saving Indicator */}
      {saving && (
        <Alert
          severity="info"
          icon={<CircularProgress size={20} />}
          sx={{ mb: 3 }}
        >
          Saving your progress...
        </Alert>
      )}

      {/* Step Content */}
      <Card>
        <CardContent sx={{ p: 4 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            renderStep()
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default IntakeWizard;
